<template>
  <div class="comment-buttons d-flex justify-content-end flex-wrap">
    <div>
      <button
        v-popover.top="{ name: `likedTooltip-${id}` }"
        data-testid="helpful-button"
        class="btn liked mt-4"
        type="button"
        :disabled="isMaxLikeCount || isFetching"
        :class="{ 'active': helpfulBtnIsActive, 'disabled-button': !isEmailVerified }"
        @click="upvoteComment"
        @keydown.enter="upvoteComment"
      >
        <div class="d-flex align-items-center">
          <font-awesome-icon
            class="comment-chevron mr-1"
            icon="chevron-up"
            width="12"
            height="12"
          />
          <span>Helpful {{ upvoteCount }}</span>
        </div>
      </button>
      <slot v-if="!isAuthenticated || !isEmailVerified">
        <client-only>
          <v-popover
            class="popover font-size-sm"
            :name="`likedTooltip-${id}`"
            event="hover"
            transition="fade"
            :hide-delay="400"
          >
            <span v-if="!isAuthenticated">
              Please <a href="/accounts/login/">sign in</a> to vote
            </span>
            <span v-else-if="!isEmailVerified">
              Please <a href="/account/email/">verify your email</a> to vote
            </span>
          </v-popover>
        </client-only>
      </slot>
    </div>
    <div
      v-if="!noReply && isAuthenticated"
      class="ml-2 mt-4"
    >
      <button
        v-popover.top="{ name: `replyTooltip-${id}` }"
        class="btn reply"
        :class="{'disabled-button': !isEmailVerified }"
        type="button"
        :disabled="isFetching"
        @click="replyComment"
        @keydown.enter="replyComment"
      >
        <span>Reply</span>
      </button>
      <slot v-if="!isEmailVerified">
        <client-only>
          <v-popover
            class="popover font-size-sm"
            :name="`replyTooltip-${id}`"
            event="hover"
            transition="fade"
            :hide-delay="400"
          >
            Please <a href="/account/email/">verify your email</a> to reply
          </v-popover>
        </client-only>
      </slot>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'PDPCommentButtons',
  components: {
    FontAwesomeIcon,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    upvoteCount: {
      type: Number,
      default: 0,
    },
    noReply: {
      type: Boolean,
      required: true,
    },
    isEmailVerified: {
      type: Boolean,
      required: true,
    },
    isAuthenticated: {
      type: Boolean,
      required: true,
    },
    helpfulBtnStartingState: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      helpfulBtnIsActive: this.helpfulBtnStartingState,
      initUpvoteCount: this.upvoteCount,
      likeCount: 0,
      maxLikeCount: 4,
    };
  },
  computed: {
    isMaxLikeCount() {
      return this.likeCount >= this.maxLikeCount;
    },
    isFetching() {
      return this.$store.getters['discussion/isFetching'];
    },
  },
  watch: {
    // Wait for response before changing helpfulBtnIsActive state
    upvoteCount() {
      // tl;dr: 'helpfulBtnIsActive' var controls the 'active' class which shows the user whether
      // the user has already toggled the 'helpful' btn for a comment
      // so,
      // if helpful btn starts active & upvote count != init count --> toggle 'active' class OFF
      // if helpful btn starts inactive & upvote count != init count --> toggle 'active' class ON
      const upvoteCountHasChanged = this.initUpvoteCount !== this.upvoteCount;
      this.helpfulBtnIsActive = upvoteCountHasChanged !== this.helpfulBtnStartingState;
    },
  },
  methods: {
    // Popover will not work if button is disabled
    upvoteComment() {
      if (this.isEmailVerified && !this.isMaxLikeCount && !this.isFetching) {
        this.$emit('update-action', { type: 'upvote', value: this.helpfulBtnIsActive });
        this.likeCount += 1;
      }
    },
    replyComment() {
      if (this.isEmailVerified && !this.isFetching) {
        this.$emit('reply-comment');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  /* stylelint-disable selector-no-qualifying-type */
  .comment-buttons button {
    min-width: 100px;
    padding: 0.4rem 1.2rem;
    font-size: 12px;
    transition: all 200ms ease-in-out;
  }

  .comment-buttons button.liked {
    border: 1px solid #272727;
    background-color: #fff;
    color: #272727;
    background-image: none;
  }

  .comment-buttons button.liked:hover,
  .comment-buttons button.liked.active {
    border: 1px solid #272727;
    background-color: #272727;
    color: #fff;
  }

  .comment-buttons button.disabled-button {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .comment-buttons button.reply.disabled-button:hover {
    border-color: #ffbc00;
    background-color: #ffbc00;
  }

  .comment-buttons button.reply {
    border-color: #ffbc00;
    background-color: #ffbc00;
    color: #272727;
  }

  .comment-buttons button.reply:hover {
    border-color: #ffcb33;
    background-color: #ffcb33;
  }

  .btn span {
    font-weight: 600;
  }
  /* stylelint-enable selector-no-qualifying-type */
</style>
